// REACT IMPORTS
import React, { useContext, useEffect, useState } from "react";

// CSS
import "./TimeOffConsole.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// ROUTER
import RouterLink from "../../Misc/GuardedRouterLink/GuardedRouterLink";

// MUI COMPONENTS
import { Button, IconButton, Tooltip } from "@mui/material";

// MUI ICONS
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";

// CUSTOM COMPONENTS
import TimeOffRequestsTab from "./TimeOffRequestsTab";
import TimeOffTypesTab from "./TimeOffTypesTab";

// CONSTANTS
import { fmtDateTime } from "./timeOffConstants";

const TABS = [
	{ id: "requests", label: "Time Off Requests" },
	{ id: "types", label: "Time Off Types" },
];

const SEVERITY_WARNING_ID = 3; // automation_log_severity

// Latest automation_log row from the sync Lambda. It logs when a run changed
// BQE or found a problem, plus an hourly heartbeat. success = 0 is a real
// failure; items that need a person are success = 1 at warning severity.
const runState = (run) => {
	if (!run)
		return {
			Icon: PauseCircleOutlineIcon,
			color: "#757575",
			label: "No Runs Logged",
		};
	if (Number(run.success) !== 1)
		return { Icon: ErrorIcon, color: "#c62828", label: "Failed" };
	if (Number(run.automation_log_severity_id) >= SEVERITY_WARNING_ID)
		return { Icon: WarningIcon, color: "#ca8a04", label: "Needs Review" };
	return { Icon: CheckCircleIcon, color: "#2e7d32", label: "OK" };
};

const SyncRunBadge = ({ run, toolName }) => {
	const { Icon, color, label } = runState(run);

	return (
		<div className="timeOffConsoleSyncBadge">
			<Icon fontSize="small" style={{ color }} />
			<div className="timeOffConsoleSyncBadgeText">
				<span className="timeOffConsoleSyncBadgeTitle">
					{toolName} · {label}
				</span>
				{run && (
					<Tooltip title={run.message || ""} arrow placement="bottom">
						<span className="timeOffConsoleSyncBadgeDetail">
							{fmtDateTime(run.created)}
							{run.message
								? ` · ${run.message.split("\n")[0]}`
								: ""}
						</span>
					</Tooltip>
				)}
			</div>
			<RouterLink
				to="/logs/automation/dashboard"
				className="timeOffConsoleSyncBadgeLink"
			>
				View Logs
			</RouterLink>
		</div>
	);
};

// REACT COMPONENT
const TimeOffConsole = () => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATE
	const [tab, setTab] = useState("requests");
	const [requestData, setRequestData] = useState(null);
	const [typeData, setTypeData] = useState(null);
	const [loadFrom, setLoadFrom] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [requestError, setRequestError] = useState("");
	const [typeError, setTypeError] = useState("");
	const [lastUpdated, setLastUpdated] = useState(null);
	const [typeDialog, setTypeDialog] = useState(null); // {} for new, a type to edit

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Time Off Console",
			employeeID: employeeID,
			notes: "Accessed the Time Off Console page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchRequests();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadFrom]);

	useEffect(() => {
		fetchTypes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// API HELPER FUNCTIONS
	const fetchRequests = async () => {
		setIsLoading(true);
		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/time-off/requests",
				loadFrom ? { from: loadFrom } : null,
			);
			setRequestData(response.data);
			setLastUpdated(Date.now());
			setRequestError("");
		} catch (err) {
			console.error(err);
			setRequestError(
				err?.response?.data?.error ||
					"Couldn't load time off requests.",
			);
		}
		setIsLoading(false);
	};

	const fetchTypes = async () => {
		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/time-off/types",
			);
			setTypeData(response.data);
			setTypeError("");
		} catch (err) {
			console.error(err);
			setTypeError(
				err?.response?.data?.error || "Couldn't load time off types.",
			);
		}
	};

	const handleRefresh = () => {
		fetchRequests();
		fetchTypes();
	};

	// Requests ending before the loaded window need a wider fetch
	const handleFromFilterChange = (from) => {
		const loaded = requestData?.from || "";
		if (from && (!loaded || from < loaded)) setLoadFrom(from);
	};

	// RENDER
	return (
		<div className="TimeOffConsole">
			<div className="timeOffConsoleHeader">
				<div className="timeOffConsoleHeaderTitle">
					Time Off Console
				</div>
				<div className="timeOffConsoleHeaderActions">
					{requestData && (
						<SyncRunBadge
							run={requestData.lastSyncRun}
							toolName={requestData.syncToolName}
						/>
					)}
					{lastUpdated && (
						<span className="timeOffConsoleUpdatedText">
							Updated {fmtDateTime(lastUpdated)}
						</span>
					)}
					<Tooltip title="Refresh" arrow placement="top">
						<span>
							<IconButton
								onClick={handleRefresh}
								disabled={isLoading}
								size="small"
							>
								<RefreshIcon />
							</IconButton>
						</span>
					</Tooltip>
				</div>
			</div>

			<div className="timeOffConsoleTabsRow">
				<div className="timeOffConsoleTabs">
					{TABS.map((t) => (
						<button
							key={t.id}
							type="button"
							className={`timeOffConsoleTab${tab === t.id ? " timeOffConsoleTab--active" : ""}`}
							onClick={() => setTab(t.id)}
						>
							{t.label}
						</button>
					))}
				</div>
				{tab === "types" && (
					<Button
						size="small"
						variant="contained"
						disableElevation
						startIcon={<AddIcon />}
						disabled={!typeData}
						onClick={() => setTypeDialog({})}
					>
						New Type
					</Button>
				)}
			</div>

			{(tab === "requests" ? requestError : typeError) && (
				<div className="timeOffConsoleError">
					{tab === "requests" ? requestError : typeError}
				</div>
			)}

			{tab === "requests" && (
				<TimeOffRequestsTab
					data={requestData}
					types={typeData?.types || []}
					isLoading={isLoading}
					onChanged={fetchRequests}
					onFromFilterChange={handleFromFilterChange}
				/>
			)}
			{tab === "types" && (
				<TimeOffTypesTab
					data={typeData}
					onChanged={fetchTypes}
					dialogType={typeDialog}
					setDialogType={setTypeDialog}
				/>
			)}
		</div>
	);
};

export default TimeOffConsole;
